import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Intent } from '@blueprintjs/core';
import { toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { loginUser } from '../../../api/auth/authApi';
import { HOME } from '../../home/routes';
import { RootState } from '../../../store/store';
import { loginRequest, loginSuccess, loginFailure } from '../../../store/authSlice';
import { clearAssociadoData } from '../../../store/associadoSlice';
import { resetCodigoData } from '../../../store/codigoSlice';
import { clearCotacaoData } from '../../../store/cotacaoSlice';
import { resetRespostasDoSga } from '../../../store/respostasDoSgaSlice';
import { consumirAvisoSessao } from '../../../utils/authAviso';

import logo from '../../../img/logo.svg';
import eyeIcon from '../../../img/eye.svg';
import closedEyeIcon from '../../../img/closed-eye.svg';

import './login.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPasswordState] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Motivo do último logout automático, deixado em sessionStorage pelo
  // `performLogout` (at-22). Fica como faixa fixa na tela — um toast não
  // sobreviveria ao reload que trouxe o usuário até aqui.
  const [avisoSessao, setAvisoSessao] = useState<string | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    dispatch(loginRequest());

    try {
      const { accessToken, firstLogin } = await loginUser(email, password);

      // O refresh token não é mais guardado no cliente (at-22): ele vem em cookie
      // httpOnly e o `/auth/refresh` o envia via `withCredentials`.
      dispatch(loginSuccess({ email, accessToken }));
      dispatch(clearAssociadoData());
      dispatch(resetCodigoData());
      dispatch(clearCotacaoData());
      dispatch(resetRespostasDoSga());

      if (firstLogin) {
        localStorage.setItem('firstLogin', 'true');
        navigate(HOME());
      } else {
        localStorage.setItem('firstLogin', 'false');
        navigate(HOME());
      }

      toast.success('Login realizado com sucesso', {
        position: 'top-right',
        autoClose: 3000,
        transition: Slide,
      });

      navigate(HOME());
    } catch (error) {
      dispatch(loginFailure());

      toast.error(
        error instanceof Error ? error.message : 'Erro no login. Verifique suas credenciais.', 
        {
          position: 'top-right',
          autoClose: 3000,
          transition: Slide,
        }
      );
    }
  };

  useEffect(() => {
    dispatch(loginFailure());
  }, []);

  // Consumo único do aviso (a chave é removida na leitura). O `if` é necessário:
  // no StrictMode o efeito roda duas vezes e a segunda leitura vem vazia — sem a
  // guarda, a mensagem apareceria e desapareceria em dev.
  useEffect(() => {
    const aviso = consumirAvisoSessao();
    if (aviso) setAvisoSessao(aviso);
  }, []);

  return (
    <div className="main-containt">
      <div className="form-container-auth">
        <img src={logo} alt="Logo Atual" />
        {avisoSessao && (
          <div className="auth-aviso-sessao" role="alert">
            {avisoSessao}
          </div>
        )}
        <form className="form-auth" onSubmit={handleSubmit}>
          <div className="auth-div">
            <div className='auth-title'>
              <h1 className="h1-auth" title="Sistema de Adesão Atual Clube">SAAC</h1>
              <p className='p-auth'>Por favor, entre com seu email e senha.</p>
            </div>
            <div className="auth-fields">
              <p>Usuário</p>
              <div className="nice-form-group auth">
                <input
                  id="email"
                  name="email"
                  className="nice-input"
                  type="text"
                  placeholder="exemplo@atualclube.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required />
              </div>
              <p>Senha</p>
              <div className="nice-form-group auth">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className="nice-input"
                  value={password}
                  onChange={(e) => setPasswordState(e.target.value)}
                  placeholder="Senha"
                  autoComplete='new-password'
                  required />
                <img
                  className='icon'
                  src={showPassword ? closedEyeIcon : eyeIcon}
                  alt="icone de olho"
                  onClick={() => setShowPassword(!showPassword)} />
              </div>
            </div>
            <div className="button-div auth">
              <Button type="submit" intent={Intent.PRIMARY} disabled={isLoading}>
                {isLoading ? 'Carregando...' : 'Entrar'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
